<template>
  <div v-if="!selectedModel" class="templates-overview">
    <el-container class="screenHeight" v-loading="modelsLoading" element-loading-background="transparent">
      <el-container direction="vertical" class="fullHeight">
        <el-scrollbar>
          <el-main>
            <div class="media">
              <div class="media-content mr-4">
                <el-input v-model="searchText" :placeholder="$t('views.template.search')" />
              </div>
              <div class="media-right" v-if="featureFlagStore.isTemplateUploadEnabled()">
                <el-button type="primary" @click="this.router.push(`/template-upload`)">
                  {{ $t('views.template.create') }}
                </el-button>
              </div>
            </div>
            <el-space wrap style="align-items: flex-start" class="productOverview">
              <ModelCard
                v-for="model in filterList"
                :key="model.modelId"
                :title="model.product.name"
                :thumbnail-url="convertToUrl(model)"
                :show-delete="isUserSupplier"
                @delete="deleteModel(model)"
                @cardClick="displayModel(model.modelId, $event)"
              />
            </el-space>
          </el-main>
        </el-scrollbar>
      </el-container>
    </el-container>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Watch } from 'vue-facing-decorator';
import { ElMessage, ElMessageBox } from 'element-plus';

import * as modelService from '@/services/api/modelService';
import {  Model2 } from '@/types/api/Model/Model';
import { FileTypeImage } from '@/types/enum/upload';
import PreviewRenderer from '@/components/three/scenes/PreviewRenderer.vue';
import PreviewRendererImg from '@/components/features/previewRenderer/PreviewRendererImg.vue';
import SideMenu from '@/components/workflow/SideMenu.vue';
import ToggleSidebar from '@/components/ui-elements/ToggleSidebar.vue';
import * as FormBuilderService from '@/services/api/formBuilderService';
import { removeWordFromString } from '@/utils/formBuilder';
import { deleteFileFromAzure } from '@/utils/file';
import { useRoute, useRouter } from 'vue-router';
import { Role } from '@/types/enum/user';
import TemplateWorkflow from './TemplateWorkflow.vue';
import { useUserStore } from '@/stores/userStore';
import { useFeatureFlagStore } from '@/stores/featureFlagStore';
import ModelCard from '@/components/ui-elements/ModelCard.vue';

@Component({
  components: {
    SideMenu,
    ToggleSidebar,
    PreviewRenderer,
    PreviewRendererImg,
    TemplateWorkflow,
    ModelCard
  },
})
/* eslint-disable @typescript-eslint/no-explicit-any*/
@Component
export default class TemplateOverview extends Vue {
  router = useRouter();
  templateList: Model2[] = [];
  FileTypeImage = FileTypeImage;
  showMenu = true;
  searchText = '';
  modelsLoading = false;
  isUserSupplier = false;

  selectedModel: Model2 | null = null;

  userStore =  useUserStore()
  featureFlagStore =  useFeatureFlagStore()

  @Watch('$route', { immediate: false })
  async onRouteChange(to: any, from: any) {
    if (from?.name === 'template-upload' && to?.name === 'templates' && to?.query?.uploaded === 'true') {
      await this.fetchTemplates(true);
      this.router.replace({ name: 'templates', query: {} });
    }
  }
  
  async mounted(): Promise<void> {
    await this.fetchTemplates(true);
  }

  async fetchTemplates(shouldRefetch = false) {
    this.isUserSupplier = this.userStore.roles.includes(Role.Supplier);

    this.modelsLoading = true;
    try {
      const userId = this.userStore.user?.pk;

      if (userId) {
        const models = await modelService.getModelsByUserId({
          userId: userId,
          productTypeId: null,
          shouldRefetch,
        });
        this.templateList = models ? models.filter((item) => !item.basedOn) : [];
      } else {
        this.templateList = [];
        console.warn('User ID not found. Cannot load templates.');
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
      this.templateList = [];
    } finally {
      this.modelsLoading = false;
    }
  }

  productTitle(title: string): string {
    return removeWordFromString(title, 'phase1');
  }

  get filterList(): Model2[] {
    if (this.searchText.length > 0)
      return this.templateList.filter((item) => item.product && item.product.name.toLowerCase().includes(this.searchText.toLowerCase()));
    return this.templateList;
  }

  convertToUrl(model: Model2): string | null {
    if (model.product && model.product.thumbnail) {
      return model.product.thumbnail.content;
    }
    return null;
  }


  async deleteFormBuilderAzureFiles(productId: number): Promise<number> {
    try {
      const res = await FormBuilderService.getProductFormBuilder(productId);
      const parsedFields = JSON.parse(res || '[]');

      for (const field of parsedFields) {
        if (field.image && field.imageType === 'file') {
          await deleteFileFromAzure(field.image);
        }
        if (field.options) {
          for (const opt of field.options) {
            if (opt.image && opt.imageType === 'file') {
              await deleteFileFromAzure(opt.image);
            }
          }
        }
      }

      return 1;
    } catch (err) {
      console.error('err delete azure form builder', err);
      return 0;
    }
  }

  async deleteModel(model: Model2): Promise<void> {

    if(!this.userStore.user?.pk) return

    try {
      await ElMessageBox.confirm(
        (this as any).$t('confirm.delete.message'),
        (this as any).$t('confirm.delete.title'),
        {
          confirmButtonText: (this as any).$t('confirm.delete.ok'),
          cancelButtonText: (this as any).$t('confirm.delete.cancel'),
          type: 'warning',
        }
      );

      const result = await modelService.deleteModel(model.modelId);

      if (result) {
        ElMessage.success((this as any).$t('success.api.delete'));
        this.templateList = this.templateList.filter((item) => item.modelId !== model.modelId);
      } else {
        ElMessage.error((this as any).$t('error.api.delete'));
      }
    } catch (err) {
      console.error('err', err);
    }
  }

  displayModel(modelId: number, event: MouseEvent): void {
    const path = event.composedPath();
    if (!path.find((item) => (item as any).id === 'deleteModel')) {
      this.selectedModel = this.templateList.find((item) => item.modelId === modelId) || null;
      this.router.push(`/template-workflow?templateId=${modelId}`)
    }
  }

  createNew(): void {
    this.router.push(`/template-workflow`);
  }

  get workflowTemplateId(): number {
    if (!this.selectedModel) return 0;
    return this.selectedModel.basedOn ? this.selectedModel.basedOn.modelId : this.selectedModel.modelId;
  }

  // Handler for when the TemplateWorkflow component signals that the user wants to go back.
  handleWorkflowBack(): void {
    this.selectedModel = null;
  }
}
</script>

<style lang="scss" scoped>
.screenHeight {
  width: 100%;
  height: 93vh;
  overflow-x: hidden;
}

.fullHeight {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.el-main {
  padding: 1.5rem;

  > .media {
    display: flex;
    gap: 0.8rem;
    padding-bottom: 1rem;
  }
  .media {
    .media-content {
      width: 100%;
      font-size: 1rem;
    }
    .media-right {
      margin-left: 0;

      .deleteModel{
        width: 30px;
        display: inline-block;
      }
    }
  }
}

.level:not(:last-child) {
  margin-bottom: unset;
}

:deep(.el-card__body) {
  display: grid;
  justify-content: center;
  padding: 0;

  .image{
    display: flex;
    justify-content: center;
  }
}

.el-container .el-container {
  max-height: calc(100vh - var(--el-header-height));
}

.products:hover {
  scale: 1.04;
}

.products {
  transition: 250ms;
  width: 100%;
  border: 0.3rem solid #f9f9f9;
  border-radius: 10px;
  cursor: pointer;
  overflow: hidden;

  .card-bottom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 0;
    padding: 0.5rem 0.2rem;
    align-items: center;
    color: #1d1e19;
    padding: 0 4px;
    background-color: #f9f9f9;
  }

  .card-title {
    padding: 0.5rem;
    font-size: clamp(0.8rem, calc(2rem - 0.15rem * var(--length, 10)), 1.2rem);
    text-align: center;
    width: 100px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.productOverview {
  padding: 0.5rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, 150px);
  grid-auto-rows: 1fr;
  justify-content: center;
  gap: 1rem 1rem;
}
</style>
